import React, { useEffect, useState } from "react";
import {
  Box,
  Drawer,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useSnackbar } from "notistack";
import {
  addTrackerTagApi,
  updateTrackerTagApi,
} from "../../../DAL/trackerTag/trackerTag";

const PRIMARY = "var(--portal-theme-primary)";
const BORDER = "#e2e8f0";
const MUTED = "#64748b";
const COLOR_DEFAULT = "#04abbb";

const emptyForm = () => ({
  name: "",
  color: COLOR_DEFAULT,
  description: "",
  status: "active",
});

export default function AddOrEditTagDrawer({ open, onClose, tag, onSaved }) {
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = Boolean(tag?._id || tag?.id);
  const [form, setForm] = useState(emptyForm());
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (tag) {
      setForm({
        name: tag.name || "",
        color: tag.color || COLOR_DEFAULT,
        description: tag.description || "",
        status: tag.status || "active",
      });
    } else {
      setForm(emptyForm());
    }
    setError("");
    setSaving(false);
  }, [open, tag]);

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const handleSave = async () => {
    const name = form.name.trim();
    if (!name) {
      setError("Tag name is required");
      return;
    }
    setSaving(true);
    setError("");
    const payload = {
      name,
      color: form.color || COLOR_DEFAULT,
      description: form.description.trim(),
      status: form.status || "active",
    };
    try {
      const res = isEdit
        ? await updateTrackerTagApi(tag._id || tag.id, payload)
        : await addTrackerTagApi(payload);
      if (res?.code === 200) {
        enqueueSnackbar(res.message || (isEdit ? "Tag updated" : "Tag added"), {
          variant: "success",
        });
        onSaved?.(res.tag);
        onClose?.();
      } else {
        setError(res?.message || "Could not save tag");
      }
    } catch (_e) {
      setError("Could not save tag");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={saving ? undefined : onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 400 },
          borderLeft: `1px solid ${BORDER}`,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.75,
          borderBottom: `1px solid ${BORDER}`,
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: 17 }}>
          {isEdit ? "Edit Tag" : "New Tag"}
        </Typography>
        <IconButton onClick={onClose} disabled={saving} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ px: 2.5, py: 2.5 }}>
        <Typography sx={{ fontSize: 13, color: MUTED, mb: 2 }}>
          Tags are shared across all trackers. Pick them on each journey instead
          of typing free text.
        </Typography>

        <TextField
          autoFocus
          fullWidth
          size="small"
          required
          label="Tag name"
          value={form.name}
          disabled={saving}
          onChange={(e) => update({ name: e.target.value.slice(0, 60) })}
          sx={{ mb: 2 }}
        />

        <Box sx={{ display: "flex", gap: 1.25, mb: 2, alignItems: "center" }}>
          <TextField
            fullWidth
            size="small"
            label="Color"
            value={form.color}
            disabled={saving}
            onChange={(e) => update({ color: e.target.value })}
          />
          <Box
            component="input"
            type="color"
            value={form.color || COLOR_DEFAULT}
            disabled={saving}
            onChange={(e) => update({ color: e.target.value })}
            sx={{
              width: 44,
              height: 40,
              border: `1px solid ${BORDER}`,
              borderRadius: "8px",
              p: 0.25,
              bgcolor: "#fff",
              cursor: "pointer",
            }}
          />
        </Box>

        <TextField
          fullWidth
          size="small"
          multiline
          minRows={2}
          label="Description (optional)"
          value={form.description}
          disabled={saving}
          onChange={(e) => update({ description: e.target.value.slice(0, 200) })}
          sx={{ mb: 2 }}
        />

        <TextField
          select
          fullWidth
          size="small"
          label="Status"
          value={form.status}
          disabled={saving}
          onChange={(e) => update({ status: e.target.value })}
          sx={{ mb: 2 }}
        >
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="inactive">Inactive</MenuItem>
        </TextField>

        {error ? (
          <Typography sx={{ color: "#dc2626", fontSize: 13, mb: 1.5 }}>
            {error}
          </Typography>
        ) : null}

        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <button
            type="button"
            className="small-contained-outlined-button"
            disabled={saving}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="small-contained-button"
            disabled={saving}
            onClick={handleSave}
            style={{ backgroundColor: PRIMARY }}
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create tag"}
          </button>
        </Box>
      </Box>
    </Drawer>
  );
}
